import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Plus, Pencil, Trash2, Calendar, Wallet, Users, User, AlertCircle, Pause, Play, ArrowLeft, Search } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import UpcomingPaymentsWidget from '../components/UpcomingPaymentsWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getSubscriptions, createSubscription, updateSubscription, deleteSubscription, getFamily } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const SUBSCRIPTION_CATEGORIES = [
  { id: 'streaming', emoji: '📺', label_fr: 'Streaming', label_en: 'Streaming' },
  { id: 'musique', emoji: '🎵', label_fr: 'Musique', label_en: 'Music' },
  { id: 'telephone', emoji: '📱', label_fr: 'Téléphone', label_en: 'Phone' },
  { id: 'internet', emoji: '🌐', label_fr: 'Internet', label_en: 'Internet' },
  { id: 'assurance', emoji: '🛡️', label_fr: 'Assurance', label_en: 'Insurance' },
  { id: 'sport', emoji: '🏋️', label_fr: 'Sport/Fitness', label_en: 'Fitness' },
  { id: 'presse', emoji: '📰', label_fr: 'Presse/Magazines', label_en: 'News/Magazines' },
  { id: 'cloud', emoji: '☁️', label_fr: 'Stockage Cloud', label_en: 'Cloud Storage' },
  { id: 'jeux', emoji: '🎮', label_fr: 'Jeux', label_en: 'Gaming' },
  { id: 'logiciel', emoji: '💻', label_fr: 'Logiciels', label_en: 'Software' },
  { id: 'transport', emoji: '🚇', label_fr: 'Transport', label_en: 'Transport' },
  { id: 'autre', emoji: '📦', label_fr: 'Autre', label_en: 'Other' },
];

// Subscription Card Component
const SubscriptionCard = ({ sub, index, user, language, t, getCategoryInfo, handleToggleActive, openEditModal, setShowDeleteConfirm, showBudgetBadge = true }) => {
  const catInfo = getCategoryInfo(sub.category);
  const isActive = sub.is_active !== false;
  const isOwn = sub.user_id === user?.user_id;
  
  return (
    <motion.div
      key={sub.subscription_id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.03 }}
    >
      <Card 
        className={isActive ? '' : 'opacity-60'}
        data-testid={`subscription-${sub.subscription_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isActive ? 'bg-primary/10' : 'bg-muted'
                }`}
              >
                <span className="text-2xl">{catInfo.emoji}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-medium ${!isActive && 'line-through text-muted-foreground'}`}>
                    {sub.name}
                  </h3>
                  {showBudgetBadge && sub.budget_type === 'common' && (
                    <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {t('common')}
                    </span>
                  )}
                  {showBudgetBadge && sub.budget_type !== 'common' && (
                    <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {t('personal')}
                    </span>
                  )}
                  {!isActive && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                      {t('paused_label')}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground mt-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span data-testid={`sub-frequency-${sub.subscription_id}`}>
                    {sub.frequency === 'weekly' ? t('freq_weekly')
                      : sub.frequency === 'quarterly' ? t('freq_quarterly')
                      : sub.frequency === 'semi_annual' ? t('freq_semi_annual')
                      : sub.frequency === 'yearly' ? t('freq_yearly')
                      : sub.frequency === 'custom_days' ? (language === 'fr' ? `Tous les ${sub.custom_days} jours` : language === 'pt' ? `A cada ${sub.custom_days} dias` : `Every ${sub.custom_days} days`)
                      : t('freq_monthly')}
                  </span>
                  <span className="text-muted-foreground/50">•</span>
                  <span>{language === 'fr' ? catInfo.label_fr : language === 'pt' ? (catInfo.label_pt || catInfo.label_en) : catInfo.label_en}</span>
                </div>
                {sub.next_payment_date && isActive && (
                  <div className="text-xs mt-1 flex items-center gap-1">
                    <span className="font-medium text-primary" data-testid={`sub-next-${sub.subscription_id}`}>
                      {t('next_payment')}: {new Date(sub.next_payment_date + 'T00:00:00').toLocaleDateString(
                        language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US',
                        { day: '2-digit', month: 'short', year: 'numeric' }
                      )}
                    </span>
                  </div>
                )}
                {sub.notes && (
                  <p className="text-sm text-muted-foreground/70 mt-1 italic">
                    {sub.notes}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              <p className={`font-bold text-xl ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {formatCurrency(sub.amount)}
              </p>
              
              {isOwn && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleToggleActive(sub)}
                    title={isActive ? t('pause_subscription') : t('reactivate')}
                  >
                    {isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 text-green-500" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEditModal(sub)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setShowDeleteConfirm(sub.subscription_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user } = useAuth();
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Form state
  const todayISO = new Date().toISOString().slice(0, 10);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    category: 'streaming',
    first_payment_date: todayISO,
    frequency: 'monthly',
    custom_days: 30,
    end_date: '',
    budget_type: 'personal',
    notes: '',
    attributed_member_ids: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter subscriptions based on search and category
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = searchQuery === '' || 
      sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || sub.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Separate subscriptions by type for hybrid mode
  const isHybridMode = family?.budget_mode === 'hybrid';
  const commonSubscriptions = filteredSubscriptions.filter(sub => sub.budget_type === 'common');
  const personalSubscriptions = filteredSubscriptions.filter(sub => sub.budget_type !== 'common');

  // Calculate totals by type
  const commonTotal = commonSubscriptions.reduce((sum, sub) => sum + (sub.is_active !== false ? sub.amount : 0), 0);
  const personalTotal = personalSubscriptions.reduce((sum, sub) => sum + (sub.is_active !== false ? sub.amount : 0), 0);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [subsRes, familyRes] = await Promise.all([
        getSubscriptions(),
        user?.family_id ? getFamily() : Promise.resolve(null)
      ]);
      
      setSubscriptions(subsRes.data.subscriptions || []);
      setTotalMonthly(subsRes.data.total_monthly || 0);
      setByCategory(subsRes.data.by_category || {});
      
      if (familyRes?.data?.family) {
        setFamily(familyRes.data.family);
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
      toast.error(t('failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  }, [user?.family_id, language]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      category: 'streaming',
      first_payment_date: new Date().toISOString().slice(0, 10),
      frequency: 'monthly',
      custom_days: 30,
      end_date: '',
      budget_type: 'personal',
      notes: '',
      attributed_member_ids: []
    });
    setEditingSubscription(null);
  };

  const openEditModal = (sub) => {
    setEditingSubscription(sub);
    setFormData({
      name: sub.name,
      amount: sub.amount.toString(),
      category: sub.category,
      first_payment_date: sub.first_payment_date || new Date().toISOString().slice(0, 10),
      frequency: sub.frequency || 'monthly',
      custom_days: sub.custom_days || 30,
      end_date: sub.end_date || '',
      budget_type: sub.budget_type || 'personal',
      notes: sub.notes || '',
      attributed_member_ids: sub.attributed_member_ids || []
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.amount) {
      toast.error(t('name_amount_required'));
      return;
    }
    if (!formData.first_payment_date) {
      toast.error(t('first_payment_date'));
      return;
    }
    if (formData.frequency === 'custom_days' && (!formData.custom_days || parseInt(formData.custom_days) < 1)) {
      toast.error(t('custom_days_label'));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        amount: parseFloat(formData.amount),
        category: formData.category,
        first_payment_date: formData.first_payment_date,
        frequency: formData.frequency,
        custom_days: formData.frequency === 'custom_days' ? parseInt(formData.custom_days) : null,
        end_date: formData.end_date || null,
        budget_type: formData.budget_type,
        notes: formData.notes,
        attributed_member_ids: formData.attributed_member_ids || []
      };
      if (editingSubscription) {
        await updateSubscription(editingSubscription.subscription_id, payload);
        toast.success(t('subscription_updated'));
      } else {
        await createSubscription(payload);
        toast.success(t('subscription_created'));
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save subscription:', error);
      toast.error(error?.response?.data?.detail || t('failed_to_save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (sub) => {
    try {
      await updateSubscription(sub.subscription_id, { is_active: !sub.is_active });
      toast.success(
        sub.is_active 
          ? t('subscription_paused')
          : t('subscription_reactivated')
      );
      loadData();
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (subscriptionId) => {
    try {
      await deleteSubscription(subscriptionId);
      toast.success(t('subscription_deleted'));
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      toast.error(t('failed_to_delete'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryInfo = (categoryId) => {
    return SUBSCRIPTION_CATEGORIES.find(c => c.id === categoryId) || SUBSCRIPTION_CATEGORIES[SUBSCRIPTION_CATEGORIES.length - 1];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl pb-24" data-testid="subscriptions-page">
      {/* Header */}
      <div className="mb-6 space-y-4">
        {/* Title row */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              {t('my_subscriptions')}
            </h1>
            <p className="text-muted-foreground">
              {t('manage_recurring_subs')}
            </p>
          </div>
        </div>
        {/* Actions row */}
        <div className="flex items-center justify-end">
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-subscription-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('add')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Monthly */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('monthly_total')}
                </p>
                <p className="text-2xl font-bold text-green-600" data-testid="total-monthly">
                  {formatCurrency(totalMonthly)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Subscriptions Count */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('active_subscriptions_label')}
                </p>
                <p className="text-2xl font-bold">
                  {subscriptions.filter(s => s.is_active !== false).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('categories')}
                </p>
                <p className="text-2xl font-bold">
                  {Object.keys(byCategory).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Payments Widget */}
      <UpcomingPaymentsWidget />

      {/* Spending by Category */}
      {Object.keys(byCategory).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">
              {t('by_category_label')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(byCategory).map(([cat, data]) => {
                const catInfo = getCategoryInfo(cat);
                const percentage = totalMonthly > 0 ? (data.total / totalMonthly) * 100 : 0;
                return (
                  <div key={cat}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2">
                        <span>{catInfo.emoji}</span>
                        <span>{language === 'fr' ? catInfo.label_fr : language === 'pt' ? (catInfo.label_pt || catInfo.label_en) : catInfo.label_en}</span>
                      </span>
                      <span className="text-muted-foreground">
                        {formatCurrency(data.total)} ({data.count})
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter Section */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('search_subscription')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="subscription-search"
              />
            </div>
            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('all_categories')}
                </SelectItem>
                {SUBSCRIPTION_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span>{cat.emoji}</span>
                      <span>{language === 'fr' ? cat.label_fr : language === 'pt' ? (cat.label_pt || cat.label_en) : cat.label_en}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(searchQuery || categoryFilter !== 'all') && (
            <p className="text-sm text-muted-foreground mt-2">
              {filteredSubscriptions.length} {t('result_count')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t('no_subscriptions_yet')}
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { resetForm(); setShowModal(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('add_subscription')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : filteredSubscriptions.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <Search className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {t('no_results_found')}
              </p>
              <Button 
                variant="link" 
                className="mt-2"
                onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }}
              >
                {t('clear_filters')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Hybrid Mode: Separate sections with totals */}
          {isHybridMode ? (
            <>
              {/* Common Subscriptions */}
              {commonSubscriptions.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-600" />
                      <h2 className="text-lg font-semibold text-green-600">
                        {t('common_subscriptions')}
                      </h2>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      {formatCurrency(commonTotal)}/mois
                    </span>
                  </div>
                  <div className="space-y-4">
                    <AnimatePresence>
                      {commonSubscriptions.map((sub, index) => (
                        <SubscriptionCard
                          key={sub.subscription_id}
                          sub={sub}
                          index={index}
                          user={user}
                          language={language}
                          t={t}
                          getCategoryInfo={getCategoryInfo}
                          handleToggleActive={handleToggleActive}
                          openEditModal={openEditModal}
                          setShowDeleteConfirm={setShowDeleteConfirm}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
              
              {/* Personal Subscriptions */}
              {personalSubscriptions.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      <h2 className="text-lg font-semibold text-blue-600">
                        {t('personal_subscriptions')}
                      </h2>
                    </div>
                    <span className="text-sm font-medium text-blue-600">
                      {formatCurrency(personalTotal)}/mois
                    </span>
                  </div>
                  <div className="space-y-4">
                    <AnimatePresence>
                      {personalSubscriptions.map((sub, index) => (
                        <SubscriptionCard
                          key={sub.subscription_id}
                          sub={sub}
                          index={index}
                          user={user}
                          language={language}
                          t={t}
                          getCategoryInfo={getCategoryInfo}
                          handleToggleActive={handleToggleActive}
                          openEditModal={openEditModal}
                          setShowDeleteConfirm={setShowDeleteConfirm}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Standard list for non-hybrid modes */
            <div className="space-y-4">
              <AnimatePresence>
                {filteredSubscriptions.map((sub, index) => (
                  <SubscriptionCard
                    key={sub.subscription_id}
                    sub={sub}
                    index={index}
                    user={user}
                    language={language}
                    t={t}
                    getCategoryInfo={getCategoryInfo}
                    handleToggleActive={handleToggleActive}
                    openEditModal={openEditModal}
                    setShowDeleteConfirm={setShowDeleteConfirm}
                    showBudgetBadge={false}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { resetForm(); setShowModal(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSubscription 
                ? t('edit') + ' ' + t('subscriptions').toLowerCase()
                : t('new_subscription')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('name')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('example_netflix')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="subscription-name-input"
              />
            </div>
            
            {/* Amount */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('monthly_amount')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="pr-8"
                  data-testid="subscription-amount-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
              </div>
            </div>
            
            {/* Category */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('category')}
              </label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="subscription-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBSCRIPTION_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        <span>{cat.emoji}</span>
                        <span>{language === 'fr' ? cat.label_fr : language === 'pt' ? (cat.label_pt || cat.label_en) : cat.label_en}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Frequency */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('frequency')}
              </label>
              <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                <SelectTrigger data-testid="subscription-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">{t('freq_weekly')}</SelectItem>
                  <SelectItem value="monthly">{t('freq_monthly')}</SelectItem>
                  <SelectItem value="quarterly">{t('freq_quarterly')}</SelectItem>
                  <SelectItem value="semi_annual">{t('freq_semi_annual')}</SelectItem>
                  <SelectItem value="yearly">{t('freq_yearly')}</SelectItem>
                  <SelectItem value="custom_days">{t('freq_custom_days')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom days */}
            {formData.frequency === 'custom_days' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('custom_days_label')} <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  min="1"
                  placeholder={t('custom_days_placeholder')}
                  value={formData.custom_days}
                  onChange={(e) => setFormData({ ...formData, custom_days: e.target.value })}
                  data-testid="subscription-custom-days-input"
                />
              </div>
            )}

            {/* First Payment Date */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('first_payment_date')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.first_payment_date}
                onChange={(e) => setFormData({ ...formData, first_payment_date: e.target.value })}
                data-testid="subscription-first-payment-input"
              />
            </div>

            {/* End Date (optional) */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('end_date_optional')}
              </label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="subscription-end-date-input"
              />
            </div>
            
            {/* Budget Type (if family with hybrid mode) */}
            {family && family.budget_mode === 'hybrid' && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('budget_type')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, budget_type: 'personal' })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.budget_type === 'personal'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-border hover:border-blue-300'
                    }`}
                  >
                    <Wallet className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{t('personal')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, budget_type: 'common' })}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      formData.budget_type === 'common'
                        ? 'border-green-500 bg-green-50'
                        : 'border-border hover:border-green-300'
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{t('common_pot')}</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Multi-Member Attribution (shared/hybrid family) */}
            {family && ['shared', 'hybrid'].includes(family.budget_mode) && family.members && family.members.length > 1 && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('attributed_to_members')} <span className="text-muted-foreground">({t('optional')})</span>
                </label>
                <div className="flex flex-wrap gap-2" data-testid="sub-attributed-picker">
                  {family.members.map((member) => {
                    const isSelected = (formData.attributed_member_ids || []).includes(member.member_id);
                    return (
                      <button
                        key={member.member_id}
                        type="button"
                        onClick={() => {
                          const cur = formData.attributed_member_ids || [];
                          setFormData({
                            ...formData,
                            attributed_member_ids: isSelected
                              ? cur.filter(id => id !== member.member_id)
                              : [...cur, member.member_id]
                          });
                        }}
                        className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 text-sm ${
                          isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`sub-attrib-${member.member_id}`}
                      >
                        <User className="w-4 h-4" />
                        <span>{member.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('notes')} <span className="text-muted-foreground">({t('optional')})</span>
              </label>
              <Input
                placeholder={t('subscription_description_placeholder')}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowModal(false); }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !formData.name || !formData.amount}>
              {isSubmitting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                editingSubscription 
                  ? (t('save'))
                  : (t('create'))
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              {t('delete_subscription_confirm')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {t('subscription_will_be_deleted')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm)}>
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
